import { API_ENDPOINT } from "@/constants/appConstants";
import { useRegistrationProps } from "@/context/useRegistrationProps";
import { useMobile, useNetwork } from "@/hooks";
import { ExistingEmailType } from "@/types/registration";
import { Details } from "@/types/userDetails";
import { encryptPayload } from "@/utils/encryptionUtils";
import { getSiteProps } from "@/utils/siteProps";
import { Button, Cross, Modal } from "@monsterindia/global-ui-components";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import ExistingEmails from "../EmailSelect/ExistingEmails";
import VerifyOTP from "./verify-otp";

type CheckExistProps = {
  payload: Details;
};

type ModelType = "email" | "mobileNumber";

type OTPData = {
  otp: string;
  otpId: string;
};

const CheckExist = forwardRef<
  {
    checkExistUser: (type: ModelType, value: any) => Promise<boolean>;
    checkMobileNumberClaimed: () => boolean;
    otpData: OTPData;
  },
  CheckExistProps
>(({ payload }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [modelType, setModelType] = useState<ModelType>("email");
  const [isClaimed, setIsClaimed] = useState(false);
  const [ExistingEmailsList, setExistingEmailsList] = useState<
    ExistingEmailType[]
  >([]);
  const {
    translation: { socialLogin },
    handleEventTracking,
    details,
    setDetails,
    hostname,
  } = useRegistrationProps();
  const siteProps = getSiteProps(hostname);
  const { fetchData: fetchAPI } = useNetwork();
  const isMobile = useMobile();

  const [isExistingEmailPopup, setIsExistingEmailPopup] = useState(false);
  const [mobileNumberClaimed, setMobileNumberClaimed] = useState(false);
  const [otpData, setOtpData] = useState<OTPData>({
    otp: "",
    otpId: "",
  });

  useEffect(() => {
    if (details.errorCode) {
      if (details.errorCode === "MOBILE_NUMBER_EXISTS") {
        setShowModal(true);
        setModelType("mobileNumber");
        setIsExistingEmailPopup(true);
        trackEvent("mobile");
      } else if (details.errorCode === "EMAIL_OR_MOBILE_EXISTS") {
        setShowModal(true);
        setModelType("email");
        setIsExistingEmailPopup(false);
        trackEvent("email");
      }
      setDetails((prevDetails: Details) => ({ ...prevDetails, errorCode: "" }));
    }
  }, [details, setDetails]);

  const closePopup = (type?: string) => {
    if (type === "continue") {
      /* cachedContactExists.current.isContinueWithReg = true;
        only show claim verify OTP for rexmonster
        otherwise let users register
      */
      if (siteProps?.site_map === "rexmonster") {
        setIsExistingEmailPopup(false);
        setIsClaimed(true);
      } else {
        /* in case of other site context, claim mobile number flow should not run
          if user clicks on Continue to register button, let the user register
        */
        setMobileNumberClaimed(true);
        // click the bottom register button
        document.getElementById("mobile-number-email")?.click();
      }
      return;
    } else if (type === "claimed_mobile_number") {
      setMobileNumberClaimed(true);
      // click the bottom register button
      document.getElementById("mobile-number-email")?.click();
      return;
    } else {
      // reset all the states
      setModelType("email");
      setIsExistingEmailPopup(false);
      setMobileNumberClaimed(false);
      setIsClaimed(false);
      setShowModal(false);
    }
  };

  const checkExistUser = async (type: ModelType, value: any) => {
    if (type === "mobileNumber") {
      setIsExistingEmailPopup(true);
    }

    setModelType(type);
    setShowModal(false);
    const reqData =
      type === "email"
        ? { email: value }
        : { countryCode: value.countryCode, mobileNumber: value.mobileNumber };

    // Encrypt the payload
    const encryptedPayload = encryptPayload(reqData);

    const url =
      API_ENDPOINT(hostname).middleware[
        type === "email" ? "checkEmailExistence" : "checkNumberExistence"
      ];

    try {
      const response = await fetchAPI(
        url,
        "POST",
        {},
        { payload: encryptedPayload },
      );
      const { message, emails } =
        (response.data as {
          message: string;
          source: string;
          emails: ExistingEmailType[];
        }) || {};

      if (message === "EXISTS") {
        if (type === "email") {
          setShowModal(true);
          if (emails && emails.length) {
            setExistingEmailsList(emails);
          }
          trackEvent(type);
          return true;
        } else {
          if (!mobileNumberClaimed) {
            setShowModal(true);
            if (emails && emails.length) {
              setExistingEmailsList(emails);
            }
            trackEvent(type);
            return true;
          }
        }
      }
      return false;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
  };

  const trackEvent = (type: string) => {
    if (type === "email") {
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Registration_Filled",
        eventProps: { Login_via_Email_Popup_Shown: true },
      });
    } else {
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Registration_Filled",
        eventProps: { Claim_Number_Popup_Shown: true },
      });
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      checkExistUser,
      checkMobileNumberClaimed: () => mobileNumberClaimed,
      otpData,
    }),
    [mobileNumberClaimed],
  );

  const crossIcon = (
    <span className="cursor-pointer">
      <Cross onClick={closePopup} height={24} width={24} color="#777584" />
    </span>
  );

  const ModalContent = useMemo(() => {
    const commonProps = {
      className: "w-full",
      modifier: "primary",
    };

    if (modelType === "email") {
      return (
        <>
          <h5 className="text-content-primary flex items-center justify-between border-b-2 p-4 text-base font-bold">
            {socialLogin.claimEmail}
            {crossIcon}
          </h5>
          <div className="px-4">
            <div className="mt-4 text-base">{socialLogin.emailExist}</div>
            <div className="text-base font-bold">{socialLogin.diffEmail}</div>
            <div className="text-content-primary my-3 text-center text-base">
              OR
            </div>
          </div>
          <div className="mt-8 px-5 py-2 shadow-[0px_-4px_8px_-2px_#00000014]">
            <Button
              label={socialLogin.loginUsingSameEmail}
              {...commonProps}
              size="lg"
              url={`/rio/login?email=${payload?.email}`}
              // url={`/rio/login`}
              id="login-using-same-email"
              onClick={() => {
                handleEventTracking({
                  eventType: "eventPush",
                  eventName: "Registration_Filled",
                  eventProps: { Login_Succesfully_via_Email_Popup: true },
                });
                if (typeof window.MessageHandler?.postMessage === "function") {
                  window.MessageHandler.postMessage(
                    JSON.stringify({
                      type: "LOGIN_PAGE",
                      isLoginScreen: true,
                    }),
                  );
                }
              }}
            />
          </div>
        </>
      );
    }

    return (
      <>
        <h5 className="text-content-primary flex items-center justify-between border-b-2 p-4 text-base font-bold">
          {socialLogin.claimNumber}
          {crossIcon}
        </h5>
        <div className="px-4">
          <div className="mt-4 text-base">{socialLogin.belongTo}</div>
          <div className="text-base font-bold">
            {socialLogin.claimThisNumber}
          </div>
          <p className="bg-state-surface-warning-subtle my-6 rounded-lg px-7 py-4 text-center text-sm md:w-96">
            {socialLogin.existDesc}
          </p>
        </div>
        <div className="mt-12 flex items-center gap-4 px-5 py-2 shadow-[0px_-4px_8px_-2px_#00000014]">
          <Button
            className="w-full"
            label={socialLogin.changeNumber}
            modifier="secondary"
            id="change-number"
            onClick={closePopup}
          />
          <Button
            label={socialLogin.claimNumber}
            {...commonProps}
            id="claim-number"
            onClick={() => setIsClaimed(true)}
          />
        </div>
      </>
    );
  }, [modelType, socialLogin, closePopup, payload]);

  const renderExistingEmailHeader = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex w-full items-center gap-1">
        <h4 className="text-base font-bold text-black md:!text-lg">
          {isExistingEmailPopup
            ? "Account already exists"
            : "Claim number to register"}
        </h4>
      </div>
    </div>
  );

  return (
    <Modal
      id="check-exist"
      header={
        isExistingEmailPopup || isClaimed ? renderExistingEmailHeader : null
      }
      isMobile={isMobile}
      showModal={showModal}
      showHandleBar={isExistingEmailPopup || isClaimed}
      closeModal={closePopup}
      showCloseButton={isExistingEmailPopup || isClaimed}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      customClass={
        isExistingEmailPopup || isClaimed
          ? `${isMobile ? "flex flex-col justify-between" : "w-[45vw] max-h-[60vh] h-auto"} bg-[#F8F9FD] rounded-b-2xl`
          : "p-0 h-auto"
      }
    >
      {isExistingEmailPopup ? (
        <ExistingEmails
          emails={ExistingEmailsList}
          payload={payload}
          closePopup={closePopup}
        />
      ) : isClaimed ? (
        <VerifyOTP
          payload={payload}
          setShowModal={setShowModal}
          closePopup={closePopup}
          onGoBackClickHandler={() => {
            setIsExistingEmailPopup(true);
            setIsClaimed(false);
          }}
          setOtpData={setOtpData}
        />
      ) : (
        ModalContent
      )}
    </Modal>
  );
});

export default CheckExist;
